import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { AmpersandProvider, InstallIntegration } from '@amp-labs/react'
// eslint-disable-next-line import/no-unresolved
import '@amp-labs/react/styles'

import { logger } from 'shared/lib'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { type Integration, IntegrationsApi } from 'entities/Integrations'
import { type IAmpersandSettings } from 'entities/Ampersand'
import { RefreshWrapper } from '../RefreshWrapper'
import styles from './styles.module.scss'

type IAmpersandConnectButton = {
  integration: Integration
  settings?: IAmpersandSettings
  onSuccess?: () => void
}

export const Ampersand = observer(
  ({ integration, settings, onSuccess }: IAmpersandConnectButton) => {
    const { usersStore } = useEntitiesContext()

    const [localData, setLocalData] = useState<IAmpersandSettings | null>(null)
    const data = settings || localData

    const [refreshApp, setRefreshApp] = useState(false)

    const getAppSettings = () => IntegrationsApi.getAmpersandAppSettings(integration.key)

    useEffect(() => {
      if (!settings) {
        getAppSettings()
          .then(({ data }) => setLocalData(data))
          .catch(console.error)
      }
    }, [])

    const consumerRef = `user-${usersStore.user?.id}`

    const getToken = async () => {
      try {
        const { data } = await getAppSettings()
        return data.jwt_token
      } catch (e) {
        logger.error(e)
        return ''
      }
    }

    const onInstallSuccess = async (installationId: string) => {
      try {
        await IntegrationsApi.createInstallation(integration.key, installationId)
        onSuccess?.()
      } catch (e) {
        logger.error(e)
      }
    }
    const onUpdateSuccess = async (installationId: string) => {
      try {
        await IntegrationsApi.updateInstallation(integration.key, installationId)
      } catch (e) {
        logger.error(e)
      }
    }
    const onUninstallSuccess = async (installationId: string) => {
      try {
        await IntegrationsApi.deleteInstallation(integration.key, installationId)
      } catch (e) {
        logger.error(e)
      }
    }

    return (
      <>
        {data && (
          <RefreshWrapper refresh={refreshApp} setRefresh={setRefreshApp}>
            <div className={styles.ampersand}>
              <AmpersandProvider
                options={{
                  project: data.project_id,
                  getToken,
                }}
              >
                <InstallIntegration
                  // The name of the integration from amp.yaml
                  integration={data.integration_name}
                  // The ID that your app uses to identify this end user.
                  consumerRef={consumerRef}
                  // The ID that your app uses to identify the user's company, org, or team.
                  groupRef={data.group_ref}
                  onInstallSuccess={onInstallSuccess}
                  onUpdateSuccess={onUpdateSuccess}
                  onUninstallSuccess={onUninstallSuccess}
                />
              </AmpersandProvider>
            </div>
          </RefreshWrapper>
        )}
      </>
    )
  }
)
